import { createFileRoute } from '@tanstack/react-router';

import {
  WorkspaceTasksPage,
  listViewOf,
  searchOfListView,
  workspaceTasksSearchSchema,
} from '@/features/workspace-tasks';
import { prefetchWorkspace } from '@/features/workspaces';

export const Route = createFileRoute('/workspaces/$workspaceId/tasks')({
  // Every filter, the search and the order are shareable and survive a reload
  // (spec 112), so they live in the address rather than in a store.
  validateSearch: workspaceTasksSearchSchema,
  // Warm the aggregate the page reads from, so arriving here does not waterfall
  // into a spinner. Failures are the component's to render, not the loader's to
  // throw.
  loader: ({ context, params }) =>
    prefetchWorkspace(context.queryClient, params.workspaceId),
  component: WorkspaceTasksRoute,
});

export function WorkspaceTasksRoute() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <WorkspaceTasksPage
      workspaceId={workspaceId}
      openTaskId={search.task}
      openTab={search.tab}
      previewPath={search.preview}
      onTabChange={(next, options) => {
        // `replace` when the drawer writes back the tab it landed on — the
        // address catching up, not a step the reader took (as on the board).
        void navigate({
          search: (prev) => ({ ...prev, tab: next }),
          replace: options?.replace ?? false,
        });
      }}
      onCloseTask={(options) => {
        // `tab` means nothing without `task`, so both go. `replace` when the
        // page closes the drawer itself because its task left the workspace:
        // Back must not reopen a drawer on a task that is no longer there. A
        // preview opened from inside the drawer goes with it.
        void navigate({
          search: (prev) => ({
            ...prev,
            task: undefined,
            tab: undefined,
            preview: undefined,
          }),
          replace: options?.replace ?? false,
        });
      }}
      onPreviewClose={() => {
        // Only the preview leaves: the drawer and its tab stay as they are.
        void navigate({
          search: (prev) => ({ ...prev, preview: undefined }),
        });
      }}
      view={listViewOf(search)}
      onViewChange={(next) => {
        void navigate({
          // The whole list view is written from scratch, so a change of filter
          // or order also closes an open drawer — including the label a reader
          // pressed inside it, which is the answer they asked to see. Absent
          // parameters are dropped: an unfiltered page carries nothing.
          search: () => searchOfListView(next),
        });
      }}
    />
  );
}
